import type { ApiErrorBody, ApiErrorCode, ApiResponse } from "@plexstore/types";

/** Error categories the UI reacts to (matches the rule set in the architecture plan). */
export type ApiErrorKind =
  | "validation"
  | "authentication"
  | "authorization"
  | "not-found"
  | "rate-limited"
  | "conflict"
  | "server"
  | "network";

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode | string;
  readonly details: Record<string, unknown> | undefined;
  readonly kind: ApiErrorKind;

  constructor(status: number, body: ApiErrorBody | undefined, fallbackMessage?: string) {
    super(body?.message ?? fallbackMessage ?? defaultMessage(status));
    this.name = "ApiError";
    this.status = status;
    this.code = body?.code ?? (status === 0 ? "NETWORK_ERROR" : "INTERNAL_ERROR");
    this.details = body?.details;
    this.kind = kindFor(status, this.code);
  }

  /** Field-level validation messages, if the API returned any. */
  get fieldErrors(): Record<string, string[]> {
    const out: Record<string, string[]> = {};
    if (!this.details) return out;
    for (const [key, value] of Object.entries(this.details)) {
      if (Array.isArray(value)) out[key] = value.map(String);
      else if (typeof value === "string") out[key] = [value];
    }
    return out;
  }

  static network(message = "Could not reach the server. Check your connection and try again."): ApiError {
    return new ApiError(0, { code: "NETWORK_ERROR", message });
  }
}

function kindFor(status: number, code: string): ApiErrorKind {
  if (status === 0) return "network";
  if (status === 401 || code === "UNAUTHENTICATED" || code === "INVALID_CREDENTIALS") return "authentication";
  if (status === 403) return "authorization";
  if (status === 404) return "not-found";
  if (status === 409) return "conflict";
  if (status === 422 || code === "VALIDATION_ERROR") return "validation";
  if (status === 429) return "rate-limited";
  return "server";
}

function defaultMessage(status: number): string {
  switch (status) {
    case 401:
      return "You need to sign in to do that.";
    case 403:
      return "You do not have permission to do that.";
    case 404:
      return "Not found.";
    case 429:
      return "Too many requests. Please slow down.";
    default:
      return "Something went wrong. Please try again later.";
  }
}

/** Unwraps the `{ data }` / `{ error }` envelope, throwing ApiError on failure. */
export async function unwrap<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;
  const contentType = response.headers.get("content-type") ?? "";
  let body: ApiResponse<T> | undefined;
  if (contentType.includes("application/json")) {
    const text = await response.text();
    if (text.trim()) {
      try {
        body = JSON.parse(text) as ApiResponse<T>;
      } catch {
        throw new ApiError(response.status, {
          code: "INTERNAL_ERROR",
          message: "The server returned an invalid response.",
        });
      }
    }
  }
  if (!response.ok) {
    const error = body && "error" in body ? body.error : undefined;
    throw new ApiError(response.status, error);
  }
  if (body && "data" in body) return body.data;
  throw new ApiError(response.status, { code: "INTERNAL_ERROR", message: "Unexpected response from the server." });
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function errorMessage(error: unknown, fallback = "Something went wrong. Please try again."): string {
  if (isApiError(error)) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
